import { CommonModule } from '@angular/common';
import {
    Component,
    input,
    output,
    forwardRef,
    ChangeDetectorRef,
    effect,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    FormsModule,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
} from '@angular/forms';
import { debounceTime, Subject } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-auto-complete',
    standalone: true,
    imports: [MaterialModule, FormsModule, ReactiveFormsModule, CommonModule],
    templateUrl: './auto-complete.component.html',
    styleUrl: './auto-complete.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AutoCompleteComponent),
            multi: true,
        },
    ],
})
export class AutoCompleteComponent implements ControlValueAccessor {
    constructor(private cdr: ChangeDetectorRef) {
        effect(() => {
            if (this.options() && this.options().length > 0 && this.firstControl.value) {
                const matchedOption: any = this.options().find(
                    (opt) => opt.id.toString() === this.firstControl.value.toString()
                );
                if (matchedOption) {
                    this.firstControl.setValue(matchedOption, { emitEvent: false });
                    this.cdr.detectChanges();
                }
            }
        });
    }

    options = input<{ name: string; id: string }[]>([]);
    label = input<string>('Select an option');
    multi = input<boolean>(false);
    onlyInput = input<boolean>(true);
    // New input for enabling clickable options
    enableDetailNavigation = input<boolean>(false);
    
    valueSelected = output<any>();
    valueChanged = output<any>();
    // New output for when user clicks on a selected option to view details
    optionClicked = output<{ name: string; id: string }>();

    firstControl = new FormControl<string | { name: string; id: string } | null>('');

    selectedOptions: { name: string; id: string }[] = [];
    value: any = '';
    onChange: any = () => {};
    onTouched: any = () => {};
    private destroy$ = new Subject<void>();

    ngOnInit(): void {
        this.firstControl.valueChanges.pipe(debounceTime(500), takeUntil(this.destroy$)).subscribe((value) => {
            if (typeof value === 'string') {
                this.valueChanged.emit(value);
                this.onChange(this.multi() ? this.selectedOptions : value);
                if(!value){
                    this.clearSelection()
                }
            }
            
        });
    }

    writeValue(value: any): void {
        if (this.multi()) {
            this.selectedOptions = value || [];
        } else {
            this.value = value;
            this.firstControl.setValue(value, { emitEvent: false });
        }
        this.cdr.detectChanges();
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.firstControl.disable();
        } else {
            this.firstControl.enable();
        }
    }

    onOptionSelected(option: { name: string; id: string }): void {
        if (this.multi()) {
            if (!this.selectedOptions.find((opt) => opt.id === option.id)) {
                this.selectedOptions.push(option);
                this.onChange(this.selectedOptions);
                this.valueSelected.emit(this.selectedOptions);
                this.firstControl.setValue('', { emitEvent: true });
                this.onChange(this.selectedOptions);
            }
            this.firstControl.setValue('', { emitEvent: false });
        } else {
            this.value = option;
            this.onChange(option);
            this.onTouched();
            this.valueSelected.emit(option);
        }
        this.cdr.detectChanges();
    }

    // New method to handle clicks on selected options for detail navigation
    onSelectedOptionClick(option: { name: string; id: string }, event: Event): void {
        if (this.enableDetailNavigation()) {
            event.stopPropagation(); // Prevent any parent event handlers
            this.optionClicked.emit(option);
        }
    }

    // Method to handle chip removal (separate from detail navigation)
    onChipRemoveClick(option: { name: string; id: string }, event: Event): void {
        event.stopPropagation(); // Prevent triggering the detail navigation
        this.removeOption(option);
    }

    removeOption(option: { name: string; id: string }): void {
        if (this.multi()) {
            this.selectedOptions = this.selectedOptions.filter((opt) => opt.id !== option.id);
            this.onChange(this.selectedOptions);
            this.valueSelected.emit(this.selectedOptions);
            this.cdr.detectChanges();
        }
    }

    displayFn(option: any): string {
        return option && option.name ? option.name : '';
    }

    onFocus(event: FocusEvent): void {
        const currentValue = this.firstControl.value;
        if (this.multi() && Array.isArray(this.selectedOptions)) {
            this.valueChanged.emit(this.selectedOptions.map((option) => option.name).join(', '));
        } else if (typeof currentValue === 'string') {
            this.valueChanged.emit(currentValue);
        } else if (currentValue && typeof currentValue === 'object' && currentValue.name) {
            this.valueChanged.emit(currentValue.name);
        }
    }

    blur(event: FocusEvent): void {
        const inputValue = (event.target as HTMLInputElement).value;
        if (!this.options().some((row) => row.name === inputValue)) {
            this.firstControl.setValue('', { emitEvent: false });
            this.cdr.detectChanges();
        }
    }

    // Method to clear selection for single select
    clearSelection(): void {
        if (!this.multi()) {
            this.value = null;
            this.firstControl.setValue('', { emitEvent: false });
            this.onChange(null);
            this.valueSelected.emit(null);
            this.cdr.detectChanges();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // Add this method to the AutoCompleteComponent class
shouldShowActions(): boolean {
    if (!this.enableDetailNavigation()) return false;
    
    // If value is an object with name property
    if (this.value && typeof this.value === 'object' && this.value.name) {
        return true;
    }
    
    // If value is a non-empty string, check if we can find matching option
    if (typeof this.value === 'string' && this.value !== '') {
        const matchedOption = this.options().find(opt => opt.id.toString() === this.value.toString());
        return !!matchedOption;
    }
    
    return false;
}

// Add this method to get the correct option object for actions
getActionOption(): { name: string; id: string } | null {
    // If value is already an object with name property
    if (this.value && typeof this.value === 'object' && this.value.name) {
        return this.value;
    }
    
    // If value is a string, find the matching option
    if (typeof this.value === 'string' && this.value !== '') {
        return this.options().find(opt => opt.id.toString() === this.value.toString()) || null;
    }
    
    return null;
}
}